import { useMutation } from "@tanstack/react-query";

import { useAppState } from "Context/StateContext";
import { useBaseRequests } from "Hooks/useBaseRequest";
import { EmailTemplate, EmailTemplateToSend } from "Models/templates/email.template";
import {
  EmailTemplateService,
  ProcessEmailTemplate,
} from "Services/templates/email.template.service";
import { UsedInEmailTemplates } from "Util/enums";

export const useRequests = () => {
  const { currentUser } = useAppState();

  const { getAll } = useBaseRequests<EmailTemplate>({
    key: "email.template",
    Service: EmailTemplateService,
  });

  const getAvailableIn = (usedIn?: string) => {
    if (usedIn) {
      if (usedIn.toLowerCase() === UsedInEmailTemplates.SHIPMENTS.toLowerCase())
        return UsedInEmailTemplates.SHIPMENTS;
      if (usedIn.toLowerCase() === UsedInEmailTemplates.ORDERS.toLowerCase())
        return UsedInEmailTemplates.ORDERS;
    }

    return UsedInEmailTemplates.QUOTES;
  };

  const getAllEmailTemplates = (usedIn?: string) => {
    return getAll([], {
      companyProfileId: currentUser?.company?.profileId,
      sortBy: JSON.stringify([["name", "asc"]]),
      include: JSON.stringify([
        {
          association: "companyToEmailTemplate",
          where: {
            companyId: currentUser?.companyId,
          },
        },
        {
          association: "templateAvailableOptions",
          where: {
            name: getAvailableIn(usedIn),
          },
        },
      ]),
    });
  };

  const sendEmailTo = () =>
    useMutation(["send.email"], (emailToSend: EmailTemplateToSend) =>
      EmailTemplateService.send(emailToSend)
    );

  const useProcessEmailTemplateRequest = (params: { onSuccess?: (params: { html: string }) => void } = {}) => {
    const { onSuccess } = params;
    return useMutation({
      mutationKey: ["process.email.template.html"],
      mutationFn: (payload: ProcessEmailTemplate) =>
        EmailTemplateService.processEmailTemplateHtml(payload).then(({ data }) => data),
      onSuccess(data) {
        onSuccess?.(data)
      }
    });
  };

  return {
    getAllEmailTemplates,
    sendEmailTo,
    useProcessEmailTemplateRequest,
  };
};
